import React, { createContext, useContext, ReactNode } from 'react';
import { useAuth, UseAuthReturn } from '../hooks/useAuth';

/**
 * Auth Context for providing authentication state and methods
 */
const AuthContext = createContext<UseAuthReturn | undefined>(undefined);

export interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Provider component that wraps your app and makes auth available
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const auth = useAuth();
  
  return (
    <AuthContext.Provider value={auth}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to use the auth context
 */
export const useAuthContext = (): UseAuthReturn => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

/**
 * Higher-order component to provide auth context
 */
export const withAuthProvider = <P extends object>(
  Component: React.ComponentType<P>
): React.FC<P> => {
  const WithAuthProvider: React.FC<P> = (props) => (
    <AuthProvider>
      <Component {...props} />
    </AuthProvider>
  );
  
  WithAuthProvider.displayName = `WithAuthProvider(${Component.displayName || Component.name})`;
  return WithAuthProvider;
};